import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  CheckIcon,
  ChevronDownIcon,
  CloudDownloadIcon,
  PlusIcon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent, CardHeader } from "simplydo/react/ui/card";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  ProjectBoardImportLanesResponse,
  ProjectBoardSetupChallengesResponse,
} from "./types";

type IProjectBoardSetup = {
  forId: string;
  lanes: OpenAPI.Schemas["ProjectLane"][];
  dataLoaded?: boolean;
  setLanes: (lanes: OpenAPI.Schemas["ProjectLane"][]) => void;
  setupModalOpen: boolean;
  setSetupModalOpen: Dispatch<SetStateAction<boolean>>;
  laneFilter?: string[];
  canManageChallenges?: boolean;
};

const ProjectBoardSetup = ({
  forId,
  lanes = [],
  dataLoaded,
  setLanes,
  setupModalOpen,
  setSetupModalOpen,
  laneFilter,
  canManageChallenges,
}: IProjectBoardSetup) => {
  // We use this flag to ensure the modal doesn't reopen once it's been closed
  const [setupHandled, setSetupHandled] = useState<boolean>(false);
  const [myChallenges, setMyChallenges] = useState<
    OpenAPI.Schemas["Challenge"][]
  >([]);
  const [importing, setImporting] = useState<boolean>(false);
  const [selectedChallengeId, setSelectedChallengeId] = useState<string>("");
  const [accordionOpen, setAccordionOpen] = useState<boolean>(false);
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;

  const { t } = useTranslation();

  const getChallenges = useCallback(() => {
    if (!userId) return;
    api.users.getChallenges(
      userId,
      { forType: "projectBoard", query: "" },
      (data: ProjectBoardSetupChallengesResponse) => {
        setMyChallenges(data.challenges);
      },
      () => {},
    );
  }, [userId]);

  const importLanes = useCallback(() => {
    setImporting(true);
    api.boards.importProjectLanes(
      forId,
      { fromBoardId: selectedChallengeId },
      (data: ProjectBoardImportLanesResponse) => {
        if (data.success) {
          setLanes(data.lanes);
          toast.success("Lanes imported successfully.");
          setImporting(false);
          setSetupModalOpen(false);
        } else {
          toast.error("Failed to import lanes.");
        }
        setImporting(false);
      },
      (err) => {
        setImporting(false);
        toast.error(err.message);
      },
    );
  }, [forId, selectedChallengeId, setLanes, setSetupModalOpen]);

  useEffect(() => {
    getChallenges();
  }, [getChallenges]);

  if (
    !canManageChallenges &&
    !util.hasPermission(user, "challenge.editProjectBoard", forId)
  ) {
    return null;
  }
  if (!dataLoaded) return null;
  if (lanes.length && lanes.length > 0) return null;
  if (setupHandled) return null;
  if (laneFilter?.length) return null;

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button
          onClick={() => setSetupModalOpen(true)}
          size="small"
          color="secondary"
        >
          <Icon icon={PlusIcon} />
          Set up project board
        </Button>
      </div>
      <Modal open={setupModalOpen} onClose={() => setSetupModalOpen(false)}>
        <ModalHeader>Welcome to the {t("generic.idea")} board</ModalHeader>
        <ModalContent scrolling>
          <p>
            This {t("generic.idea")} board is not setup yet. You may import the
            lane structure from another {t("generic.challenge")} you manage, or
            start from scratch.
          </p>
          <Accordion styled style={{ marginTop: 16 }}>
            <AccordionTitle
              active={accordionOpen}
              onClick={() => setAccordionOpen((prevOpen) => !prevOpen)}
            >
              Select {t("generic.challengeWithArticle")} to import from
            </AccordionTitle>
            <AccordionContent active={accordionOpen}>
              <Grid columns={2} mobile={1}>
                {myChallenges.map((c) => (
                  <GridColumn key={c._id}>
                    <Card
                      fluid
                      onClick={() =>
                        setSelectedChallengeId((prevSelected) =>
                          prevSelected === c._id ? "" : c._id,
                        )
                      }
                    >
                      <Button
                        color={
                          selectedChallengeId === c._id ? "violet" : undefined
                        }
                        style={{ position: "absolute", top: 5, left: 5 }}
                      >
                        <Icon icon={CheckIcon} />
                      </Button>
                      <div
                        style={{
                          height: 100,
                          backgroundPosition: "center center",
                          backgroundSize: "cover",
                          backgroundImage: `url(${util.mixinCssUrlFallback(util.challengeImage(c), util.challengeImage())})`,
                        }}
                      />
                      <CardHeader>
                        <H4>{c.name}</H4>
                        <Subheader>
                          Copy the lane structure from this{" "}
                          {t("generic.challenge")}
                        </Subheader>
                      </CardHeader>
                      <CardContent>
                        <ButtonLink
                          variant="soft"
                          to={`/challenges/${c._id}/board`}
                          target="_blank"
                        >
                          View {t("generic.idea")} board
                        </ButtonLink>
                      </CardContent>
                    </Card>
                  </GridColumn>
                ))}
              </Grid>
            </AccordionContent>
          </Accordion>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setSetupModalOpen(false)}>Cancel</Button>

          <Button
            color="primary"
            loading={importing}
            onClick={() => importLanes()}
            disabled={!selectedChallengeId}
          >
            <Icon icon={CloudDownloadIcon} />
            {`Import from selected ${t("generic.challenge")}`}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default ProjectBoardSetup;
